/**
 * Fatiado de pages/Menu.tsx por responsabilidade (#131, R3).
 */
import { useState } from 'react'
import type { useQueryClient } from '@tanstack/react-query'
import { useMutation } from '@tanstack/react-query'
import { products as prodApi, getImageUrl, type Product } from '../../lib/api.js'
import { toast } from '../../components/ui/index.js'
import { useI18n } from '../../lib/i18n.js'
// ─── Images editor ────────────────────────────────────────────────────────────

export function ImagesEditor({
  product,
  qc,
  onProductUpdate,
}: {
  product: Product
  qc: ReturnType<typeof useQueryClient>
  onProductUpdate: (p: Product) => void
}) {
  const { t } = useI18n()
  const [uploading, setUploading] = useState(false)

  const deleteMut = useMutation({
    mutationFn: (imageId: string) => prodApi.deleteImage(product.id, imageId),
    onSuccess: (_, imageId) => {
      void qc.invalidateQueries({ queryKey: ['products'] })
      onProductUpdate({ ...product, images: product.images.filter((i) => i.id !== imageId) })
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const primaryMut = useMutation({
    mutationFn: (imageId: string) => prodApi.setPrimaryImage(product.id, imageId),
    onSuccess: (updated) => {
      void qc.invalidateQueries({ queryKey: ['products'] })
      onProductUpdate({
        ...product,
        images: product.images.map((i) => ({ ...i, is_primary: i.id === updated.id })),
      })
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? [])
    if (files.length === 0) return
    setUploading(true)
    try {
      let updatedProduct = product
      for (const file of files) {
        const img = await prodApi.uploadImage(product.id, file)
        updatedProduct = { ...updatedProduct, images: [...updatedProduct.images, img] }
      }
      void qc.invalidateQueries({ queryKey: ['products'] })
      onProductUpdate(updatedProduct)
    } catch (e) {
      toast.error((e as Error).message)
    } finally {
      setUploading(false)
      e.target.value = ''
    }
  }

  return (
    <div className="rounded-xl border border-gray-200 p-4">
      <p className="text-sm font-medium text-gray-700 mb-3">{t('admin.menu.images')}</p>
      {product.images.length === 0 ? (
        <p className="text-xs text-gray-400 mb-3">{t('admin.menu.no_images')}</p>
      ) : (
        <div className="grid grid-cols-3 gap-2 mb-3">
          {product.images.map((img) => (
            <div
              key={img.id}
              className="relative aspect-square rounded-lg overflow-hidden cursor-pointer group"
              onClick={() => !img.is_primary && primaryMut.mutate(img.id)}
            >
              <img
                src={img.url ?? getImageUrl(img.r2_key)}
                alt=""
                className="w-full h-full object-cover"
              />
              {img.is_primary && (
                <span
                  className="absolute top-1 left-1 text-yellow-400 text-base leading-none pointer-events-none select-none"
                  title={t('admin.menu.primary_image')}
                >
                  ★
                </span>
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  deleteMut.mutate(img.id)
                }}
                className="absolute top-1 right-1 w-5 h-5 rounded-full bg-white/80 hover:bg-red-100 text-gray-600 hover:text-red-500 flex items-center justify-center text-xs leading-none opacity-0 group-hover:opacity-100 transition-opacity"
                title={t('admin.menu.remove_image')}
              >
                ×
              </button>
              {!img.is_primary && (
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors pointer-events-none" />
              )}
            </div>
          ))}
        </div>
      )}
      <label
        className={`inline-flex items-center gap-2 cursor-pointer px-3 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
      >
        {uploading ? t('admin.menu.uploading') : t('admin.menu.add_image')}
        <input
          data-testid="catalog-image-upload"
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={handleFileChange}
          disabled={uploading}
        />
      </label>
    </div>
  )
}
